// Applications API service
// Handles CRUD operations and paginated queries for applications.
// HTTP, envelope unwrapping and ApiError mapping come from the shared @startus/lib client.

import type {
  Application,
  ApplicationCountResponse,
  CreateApplicationPayload,
  UpdateApplicationPayload,
  ApplicationQueryParams,
  PaginatedResponse,
} from './types';
import { apiRequest, apiRequestData, apiRequestPaginated } from '@startus/lib';

// Re-export shared ApiError so hooks that import from this file keep working
export type { ApiError } from '@startus/lib';

// Build query string — maps FE param names to BE param names.
function buildQueryString(params: ApplicationQueryParams): string {
  const qs = new URLSearchParams();

  if (params.page) qs.append('page', params.page.toString());
  if (params.limit) qs.append('limit', params.limit.toString());
  if (params.search) qs.append('search', params.search);
  if (params.status) qs.append('status', params.status);
  if (params.sortBy) qs.append('orderBy', params.sortBy);
  if (params.sortOrder) qs.append('sortDir', params.sortOrder);

  return qs.toString();
}

// Get applications with pagination and filters
// Uses /company/{id} path when companyId provided (backend doesn't accept it as query param)
export async function getApplications(
  params: ApplicationQueryParams = {}
): Promise<PaginatedResponse<Application>> {
  const queryString = buildQueryString(params);
  const basePath = params.companyId
    ? `/api/v1/applications/company/${params.companyId}`
    : '/api/v1/applications';

  return apiRequestPaginated<Application>(`${basePath}${queryString ? `?${queryString}` : ''}`);
}

// Create new application
export async function createApplication(payload: CreateApplicationPayload): Promise<Application> {
  return apiRequestData<Application>('/api/v1/applications', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

// Update existing application
export async function updateApplication(
  id: string,
  payload: UpdateApplicationPayload
): Promise<Application> {
  return apiRequestData<Application>(`/api/v1/applications/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

// Get application counts grouped by status for the current user
// companyId: optional — scopes to a specific company; omit for all user's companies
export async function getApplicationCount(companyId?: string): Promise<ApplicationCountResponse> {
  const query = companyId ? `?companyId=${companyId}` : '';
  return apiRequestData<ApplicationCountResponse>(`/api/v1/applications/count${query}`);
}

// Delete application
export async function deleteApplication(id: string): Promise<void> {
  await apiRequest(`/api/v1/applications/${id}`, { method: 'DELETE' });
}

// Service object export
export const applicationsService = {
  getAll: getApplications,
  getCount: (companyId?: string) => getApplicationCount(companyId),
  // Legacy: fetches by company using query param
  getByCompany: (companyId: string) => getApplications({ companyId }),
  create: createApplication,
  update: updateApplication,
  delete: deleteApplication,
};
